import { signal } from "@preact/signals";

import type {
  SearchFilters,
  SearchIndexWorkerRequest,
  SearchIndexWorkerResponse,
  SearchResponse,
  SerializedSearchIndexError,
} from "./SearchIndexProtocol.js";

interface PendingSearch {
  readonly resolve: (response: SearchResponse) => void;
  readonly reject: (reason: unknown) => void;
}

function extensionOf(name: string): string {
  return name.toLowerCase().split(".").at(-1) ?? "";
}

function deserializeError(serialized: SerializedSearchIndexError): Error {
  const error = new Error(serialized.message);
  error.name = serialized.name;
  if (serialized.stack !== null) {
    error.stack = serialized.stack;
  }
  return error;
}

/** Main-thread proxy for the incremental symbol index worker. */
export class SearchIndexService {
  public readonly revision = signal(0);
  public readonly indexingArtifactCount = signal(0);

  private readonly worker: Worker;
  private readonly pendingSearches = new Map<number, PendingSearch>();
  private nextRequestId = 1;
  private workerFailure: Error | null = null;

  public constructor() {
    this.worker = new Worker(
      new URL("./SearchIndexWorker.ts", import.meta.url),
      {
        name: "jd-core-search-index",
        type: "module",
      },
    );
    this.worker.onmessage = (
      event: MessageEvent<SearchIndexWorkerResponse>,
    ): void => {
      this.handleResponse(event.data);
    };
    this.worker.onerror = (event: ErrorEvent): void => {
      this.failWorker(new Error(event.message || "The search index worker failed"));
    };
    this.worker.onmessageerror = (): void => {
      this.failWorker(
        new Error("The search index worker returned an unreadable message"),
      );
    };
  }

  public registerArtifact(artifactId: string, file: File): void {
    const extension = extensionOf(file.name);
    if (
      extension === "jmod" ||
      (extension !== "class" && extension !== "jar" && extension !== "zip")
    ) {
      this.unregisterArtifact(artifactId);
      return;
    }

    this.postRequest({
      type: "index-artifact",
      artifactId,
      name: file.name,
      blob: file,
    });
  }

  public unregisterArtifact(artifactId: string): void {
    this.postRequest({ type: "remove-artifact", artifactId });
  }

  public clear(): void {
    this.postRequest({ type: "clear" });
  }

  public search(
    query: string,
    filters: SearchFilters,
  ): Promise<SearchResponse> {
    if (this.workerFailure !== null) {
      return Promise.reject(this.workerFailure);
    }

    const requestId = this.nextRequestId++;
    const request: SearchIndexWorkerRequest = {
      type: "search",
      requestId,
      query,
      filters: {
        targetTypes: [...filters.targetTypes],
        usages: [...filters.usages],
      },
    };

    return new Promise<SearchResponse>((resolve, reject) => {
      this.pendingSearches.set(requestId, { resolve, reject });
      this.worker.postMessage(request);
    });
  }

  private postRequest(request: SearchIndexWorkerRequest): void {
    if (this.workerFailure === null) {
      this.worker.postMessage(request);
    }
  }

  private handleResponse(response: SearchIndexWorkerResponse): void {
    switch (response.type) {
      case "index-state":
        this.revision.value = response.revision;
        this.indexingArtifactCount.value = response.indexingArtifactCount;
        return;
      case "index-failed":
        this.revision.value = response.revision;
        this.indexingArtifactCount.value = response.indexingArtifactCount;
        console.error(
          `Unable to index artifact ${response.artifactId}`,
          deserializeError(response.error),
        );
        return;
      case "search-results": {
        const pending = this.pendingSearches.get(response.requestId);
        if (pending === undefined) {
          return;
        }
        this.pendingSearches.delete(response.requestId);
        pending.resolve(response.response);
        return;
      }
      case "search-failed": {
        const pending = this.pendingSearches.get(response.requestId);
        if (pending === undefined) {
          return;
        }
        this.pendingSearches.delete(response.requestId);
        pending.reject(deserializeError(response.error));
      }
    }
  }

  private failWorker(reason: unknown): void {
    if (this.workerFailure !== null) {
      return;
    }
    this.workerFailure =
      reason instanceof Error ? reason : new Error(String(reason));
    this.indexingArtifactCount.value = 0;
    for (const pending of this.pendingSearches.values()) {
      pending.reject(this.workerFailure);
    }
    this.pendingSearches.clear();
  }
}
